import type { Department } from "../../../contexts/AuthProvider";
import { multiSortComparator, type SortingState } from "../../../utils/multiColumnSorting";
import type { ScheduleRow } from "../types";
import { projectForSort, type SortProjection } from "./sortKey";

interface SortRowsArgs {
	rows: ScheduleRow[];
	sortState: SortingState<SortProjection>[];
	customStringOrderMap: Map<string, number> | null | undefined;
	jobManagementEnabled: boolean;
	department: Department;
}

/**
 * Sort schedule rows using the shared `multiSortComparator` over the unified `SortProjection` shape.
 *
 * This is **the only row-sort comparator** in the printout pipeline — both the configured-sort
 * source and the DaySchedule UI fallback feed the same `SortingState<SortProjection>[]` shape
 * (see `sourceSelector.ts`), so there is no second comparator path (mapping §5.8).
 *
 * Each row is paired with its projection up front; we sort the pairs (so `Array.prototype.sort`
 * stability covers both projection comparisons and row identity), then drop the projections.
 */
export const sortRows = (args: SortRowsArgs): ScheduleRow[] => {
	const { rows, sortState, customStringOrderMap, jobManagementEnabled, department } = args;
	if (sortState.length === 0) return rows;

	const comparator = multiSortComparator<SortProjection>(sortState, customStringOrderMap);
	const paired = rows.map((row) => ({ row, projection: projectForSort({ row, jobManagementEnabled, department }) }));
	paired.sort((a, b) => comparator(a.projection, b.projection));
	return paired.map((p) => p.row);
};
